import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FileText, Eye, CheckCircle, Trash2, Edit, User, Briefcase, MapPin, CalendarDays } from 'lucide-react';
import axios from 'axios';
import ConfirmModal from './ConfirmModal';
import Notification from './Notification';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const QuoteHistory = () => {
  const navigate = useNavigate();
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notification, setNotification] = useState(null);
  const [confirmModal, setConfirmModal] = useState(null);

  const showNotification = (type, message) => {
    setNotification({ type, message });
    setTimeout(() => setNotification(null), 5000);
  };

  const showConfirm = (config) => {
    return new Promise((resolve) => {
      setConfirmModal({
        ...config,
        onConfirm: () => {
          setConfirmModal(null);
          resolve(true);
        },
        onCancel: () => {
          setConfirmModal(null);
          resolve(false);
        }
      });
    });
  };

  useEffect(() => {
    loadQuotes();
  }, []);

  const loadQuotes = async () => {
    try {
      const token = localStorage.getItem('session_token');
      const response = await axios.get(`${API}/quotes`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setQuotes(response.data);
    } catch (error) {
      console.error('Error loading quotes:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleApproval = async (quoteId, e) => {
    e.stopPropagation();
    try {
      const token = localStorage.getItem('session_token');
      const response = await axios.put(
        `${API}/quotes/${quoteId}/approve`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Update local state
      setQuotes(quotes.map(q => 
        q.id === quoteId ? { ...q, approved: response.data.approved } : q
      ));
      showNotification('success', response.data.approved ? 'Quote approved!' : 'Quote unapproved');
    } catch (error) {
      console.error('Error toggling approval:', error);
      showNotification('error', 'Failed to update approval status');
    }
  };

  const handleEditQuote = (quote, e) => {
    e.stopPropagation();
    // Navigate to home with quote data in state for editing
    navigate('/', { state: { editQuote: quote } });
  };

  const handleDeleteQuote = async (quoteId, quoteNumber, e) => {
    e.stopPropagation();
    
    const confirmed = await showConfirm({
      title: 'Delete Quote',
      message: `Are you sure you want to delete quote ${quoteNumber}? This action cannot be undone.`,
      confirmText: 'Delete',
      confirmColor: '#dc3545'
    });

    if (!confirmed) return;

    try {
      const token = localStorage.getItem('session_token');
      await axios.delete(`${API}/quotes/${quoteId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Remove from local state
      setQuotes(quotes.filter(q => q.id !== quoteId));
      showNotification('success', `Quote ${quoteNumber} deleted successfully`);
    } catch (error) {
      console.error('Error deleting quote:', error);
      showNotification('error', 'Failed to delete quote');
    }
  };

  if (loading) {
    return <div>Loading...</div>;
  }

  if (quotes.length === 0) {
    return (
      <div className="form-container" style={{ textAlign: 'center' }}>
        <FileText size={64} style={{ color: '#ccc', margin: '2rem auto' }} />
        <h2 style={{ color: '#666', marginBottom: '1rem' }}>No Quotes Yet</h2>
        <p style={{ color: '#999', fontSize: '1.1rem', marginBottom: '2rem' }}>
          Create your first quote to see it here
        </p>
        <button onClick={() => navigate('/')} className="btn btn-primary">
          <FileText size={20} />
          Create New Quote
        </button>
      </div>
    );
  }

  return (
    <div className="form-container">
      {notification && (
        <Notification
          type={notification.type}
          message={notification.message}
          onClose={() => setNotification(null)}
        />
      )}
      
      {confirmModal && (
        <ConfirmModal
          title={confirmModal.title}
          message={confirmModal.message}
          confirmText={confirmModal.confirmText}
          confirmColor={confirmModal.confirmColor}
          onConfirm={confirmModal.onConfirm}
          onCancel={confirmModal.onCancel}
        />
      )}

      <div className="form-header">
        <h2>Recent Quotes</h2>
        <p>Your last 5 quotes</p>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
        {quotes.map((quote) => (
          <div
            key={quote.id}
            style={{
              display: 'flex',
              gap: '1rem',
              padding: '1.35rem 1.5rem',
              backgroundColor: '#ffffff',
              border: '1px solid #e2e8f0',
              borderRadius: '16px',
              cursor: 'pointer',
              transition: 'border-color 0.2s, box-shadow 0.2s',
              position: 'relative',
              boxShadow: '0 6px 18px rgba(15, 23, 42, 0.06)'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.borderColor = '#c7ddff';
              e.currentTarget.style.boxShadow = '0 12px 24px rgba(15, 23, 42, 0.08)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = '#e2e8f0';
              e.currentTarget.style.boxShadow = '0 6px 18px rgba(15, 23, 42, 0.06)';
            }}
            onClick={() => navigate(`/preview/${quote.id}`)}
          >
            <div style={{
              width: '6px',
              borderRadius: '999px',
              backgroundColor: quote.approved ? '#28a745' : '#4A90E2'
            }} />

            <div style={{ flex: 1, minWidth: '0' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: '1rem', flexWrap: 'wrap' }}>
                <div>
                  <h3 style={{ fontSize: 'clamp(1.2rem, 4vw, 1.5rem)', color: '#1f2937', marginBottom: '0.4rem' }}>
                    {quote.quote_number}
                  </h3>
                  <div style={{ display: 'grid', gap: '0.35rem' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: '#475569', fontSize: '0.95rem' }}>
                      <User size={16} />
                      {quote.customer_name}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: '#475569', fontSize: '0.95rem' }}>
                      <Briefcase size={16} />
                      {quote.service_type}
                    </div>
                    {quote.location && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: '#475569', fontSize: '0.95rem' }}>
                        <MapPin size={16} />
                        {quote.location}
                      </div>
                    )}
                  </div>
                </div>

                <div style={{ textAlign: 'right', minWidth: '160px' }}>
                  <div style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '0.35rem',
                    padding: '0.3rem 0.85rem',
                    borderRadius: '999px',
                    backgroundColor: quote.approved ? '#28a745' : '#e2e8f0',
                    color: quote.approved ? '#ffffff' : '#475569',
                    fontSize: '0.8rem',
                    fontWeight: '600'
                  }}>
                    <CheckCircle size={14} />
                    {quote.approved ? 'Approved' : 'Pending'}
                  </div>
                  <div style={{ marginTop: '0.6rem' }}>
                    <div style={{ fontSize: '0.8rem', color: '#94a3b8' }}>Total</div>
                    <div style={{ fontSize: 'clamp(1.2rem, 4vw, 1.6rem)', fontWeight: '700', color: quote.approved ? '#28a745' : '#4A90E2' }}>
                      ${parseFloat(quote.price).toFixed(2)}
                    </div>
                  </div>
                </div>
              </div>

              <div style={{
                marginTop: '1rem',
                paddingTop: '0.75rem',
                borderTop: '1px solid #e2e8f0',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: '1rem',
                flexWrap: 'wrap'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: '#94a3b8', fontSize: '0.9rem' }}>
                  <CalendarDays size={16} />
                  {quote.date}
                </div>
                <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                  <button
                    className="btn btn-primary"
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(`/preview/${quote.id}`);
                    }}
                    style={{ padding: '0.4rem 0.9rem', fontSize: '0.85rem' }}
                  >
                    <Eye size={14} />
                    View
                  </button>
                  <button
                    onClick={(e) => handleEditQuote(quote, e)}
                    style={{
                      padding: '0.4rem 0.9rem',
                      fontSize: '0.85rem',
                      fontWeight: '600',
                      color: '#1f2937',
                      backgroundColor: '#e2e8f0',
                      border: 'none',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.25rem'
                    }}
                  >
                    <Edit size={14} />
                    Edit
                  </button>
                  <button
                    onClick={(e) => handleToggleApproval(quote.id, e)}
                    style={{
                      padding: '0.4rem 0.9rem',
                      fontSize: '0.85rem',
                      fontWeight: '600',
                      color: 'white',
                      backgroundColor: quote.approved ? '#6c757d' : '#28a745',
                      border: 'none',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.25rem'
                    }}
                  >
                    <CheckCircle size={14} />
                    {quote.approved ? 'Unapprove' : 'Approve'}
                  </button>
                  <button
                    onClick={(e) => handleDeleteQuote(quote.id, quote.quote_number, e)}
                    style={{
                      padding: '0.4rem 0.9rem',
                      fontSize: '0.85rem',
                      fontWeight: '600',
                      color: 'white',
                      backgroundColor: '#dc3545',
                      border: 'none',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.25rem'
                    }}
                  >
                    <Trash2 size={14} />
                    Delete
                  </button>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default QuoteHistory;
